import { Capacitor } from '@capacitor/core'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { OfflineStorage } from 'src/services/offline-storage'
import { compressBase64 } from 'src/utils'
import { withAnswersLock } from '@/services/offline-answers-lock'

const IMAGES_FOLDER = 'offline_images'
const MAX_BASE64_SIZE = 4_000_000

// Prefijo de un data URL de imagen en base64 con al menos un caracter de payload. Solo se examina el
// inicio del string; el payload no se recorre.
const IMAGE_DATA_URL_BASE64 = /^data:image\/[\w.+-]+;base64,[A-Za-z0-9+/]/

const base64ToBlob = base64 => {
  try {
    if (!base64 || typeof base64 !== 'string') {
      return new Blob([], { type: 'image/jpeg' })
    }

    let cleaned = base64.trim()

    if (!cleaned.startsWith('data:image')) {
      const comma = cleaned.indexOf(',')
      if (comma > -1) cleaned = 'data:image/jpeg;base64,' + cleaned.substring(comma + 1)
      else return new Blob([], { type: 'image/jpeg' })
    }

    const commaIndex = cleaned.indexOf(',')
    const raw = cleaned.substring(commaIndex + 1)
    const binary = atob(raw)
    const bytes = new Uint8Array(binary.length)

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.codePointAt(i)
    }

    return new Blob([bytes], { type: 'image/jpeg' })
  } catch (e) {
    console.error(e)
    return new Blob([], { type: 'image/jpeg' })
  }
}

const persistOfflineAnswer = addon =>
  withAnswersLock(async () => {
    const offline = await OfflineStorage.getAnswers() || []

    const idx = offline.findIndex(a =>
      Number(a.occurrence_id) === Number(addon.occurrence_id) &&
      Number(a.item_id) === Number(addon.item_id) &&
      (a.type || null) === (addon.type || null)
    )

    if (idx > -1) offline.splice(idx, 1)

    const cleanAddon = {
      occurrence_id: addon.occurrence_id,
      form_assignment_location_occurrence_id: addon.form_assignment_location_occurrence_id,
      item_id: addon.item_id,
      type: addon.type || null,
      answer_ref: addon.answer_ref || null,
      image_ref: addon.image_ref || null,
      localPath: addon.localPath || null,
      thumbnail: addon.thumbnail || null,
      value: null,
      temp: addon.temp || false,
      editing: addon.editing || false
    }

    offline.push(cleanAddon)
    await OfflineStorage.setAnswers(offline)
    return cleanAddon
  })

const persistOfflineAnswerWeb = addon =>
  withAnswersLock(async () => {
    // Web: blob URLs son efímeras. Convertir a base64 para persistencia estable.
    let stableValue = addon.value
    if (typeof addon.value === 'string' && addon.value.startsWith('blob:')) {
      try {
        const res = await fetch(addon.value)
        if (res.ok) {
          const blobData = await res.blob()
          stableValue = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(blobData)
          })
        }
      } catch (e) {
        console.warn('[persistOfflineAnswerWeb] blob URL → base64 fallida:', e)
      }
    }

    const offline = await OfflineStorage.getAnswers() || []

    const idx = offline.findIndex(a =>
      Number(a.occurrence_id) === Number(addon.occurrence_id) &&
      Number(a.item_id) === Number(addon.item_id) &&
      (a.type || null) === (addon.type || null)
    )

    if (idx > -1) offline.splice(idx, 1)

    const cleanAddon = { ...addon, localPath: null, value: stableValue }

    offline.push(cleanAddon)
    await OfflineStorage.setAnswers(offline)
    return cleanAddon
  })

export const saveOfflineImage = async addon => {
  const isPhotoItem =
    addon?.item_type === 'photo' ||
    addon?.input_type === 'photo' ||
    addon?.forceBase64 === true

  if (isPhotoItem) {
    return withAnswersLock(async () => {
      const offline = await OfflineStorage.getAnswers() || []
      offline.push({
        occurrence_id: addon.occurrence_id,
        form_assignment_location_occurrence_id: addon.form_assignment_location_occurrence_id,
        item_id: addon.item_id,
        type: null,
        answer_ref: null,
        image_ref: addon.image_ref || `img-${Date.now()}-${addon.item_id}`,
        localPath: null,
        value: addon.value,
        temp: false,
        editing: false,
        _uid: Date.now() + Math.random()
      })
      await OfflineStorage.setAnswers(offline)
    })
  }

  if (!Capacitor.isNativePlatform()) {
    // Web: sin IDB, solo persistir metadatos del addon
    return persistOfflineAnswerWeb(addon)
  }

  // Nativo — si tenemos Blob pero no localPath, guardar al filesystem directamente
  if (!addon.localPath && addon.blob instanceof Blob) {
    try {
      const fileName = addon.answer_ref || addon.image_ref || `img-${Date.now()}-${addon.item_id}`
      const localPath = await saveImageToFileSystem(fileName, addon.blob)
      addon.localPath = localPath
    } catch (e) { console.error(e) }
  }

  // Cordova — legacy: base64 en value sin localPath (addons pre-B.5)
  if (!addon.localPath && addon.value && typeof addon.value === 'string' && addon.value.startsWith('data:image')) {
    try {
      const fileName = addon.answer_ref || addon.image_ref || `img-${Date.now()}-${addon.item_id}`
      const localPath = await saveImageToFileSystem(fileName, addon.value)
      addon.localPath = localPath
      addon.value = null
    } catch (e) { console.error(e) }
  }

  return persistOfflineAnswer(addon)
}

const blobToBase64 = blob => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve(reader.result.split(',')[1])
  reader.onerror = reject
  reader.readAsDataURL(blob)
})

// Acepta Blob directamente o base64 string (retrocompatibilidad)
export const saveImageToFileSystem = async (fileName, source) => {
  if (!Capacitor.isNativePlatform()) throw new Error('Solo dispositivo')

  if (!fileName.endsWith('.jpeg')) fileName += '.jpeg'

  // Un data URL de imagen ya trae el base64 que pide Filesystem.writeFile: se escribe su payload tal cual,
  // sin decodificarlo a Blob para volver a codificarlo. Cualquier otra fuente (Blob, texto sin prefijo
  // reconocible) sigue por el camino de siempre.
  const base64 = typeof source === 'string' && IMAGE_DATA_URL_BASE64.test(source)
    ? source.slice(source.indexOf(',') + 1)
    : await blobToBase64(source instanceof Blob ? source : base64ToBlob(source))

  const { uri } = await Filesystem.writeFile({
    path: `${IMAGES_FOLDER}/${fileName}`,
    data: base64,
    directory: Directory.Data,
    recursive: true
  })

  return uri
}

export const readImageFromFileSystem = async localPath => {
  if (!Capacitor.isNativePlatform()) throw new Error('Solo disponible en móvil')
  if (!localPath) return null

  if (localPath.startsWith('content://')) {
    return new Promise(resolve => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', localPath)
      xhr.responseType = 'blob'
      xhr.onload = () => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const result = reader.result
          if (!result || typeof result !== 'string') return resolve(null)
          if (result.startsWith('data:image')) return resolve(result)
          resolve(null)
        }
        reader.onerror = () => resolve(null)
        reader.readAsDataURL(xhr.response)
      }
      xhr.onerror = () => resolve(null)
      xhr.send()
    })
  }

  try {
    const { data } = await Filesystem.readFile({ path: localPath })
    return `data:image/jpeg;base64,${data}`
  } catch {
    return null
  }
}

// Versión Blob de readImageFromFileSystem — sin FileReader, sin base64.
// Usar en código nuevo (formularios Acción B).
// readImageFromFileSystem sigue disponible para tareas/sprints/quickChecks.
export const readImageAsBlob = async localPath => {
  if (!Capacitor.isNativePlatform()) throw new Error('Solo disponible en móvil')
  if (!localPath) return null

  // content:// URIs (cámara/galería) — XHR funciona directamente
  if (localPath.startsWith('content://')) {
    return new Promise(resolve => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', localPath)
      xhr.responseType = 'blob'
      xhr.onload = () => resolve(xhr.response instanceof Blob && xhr.response.size > 0 ? xhr.response : null)
      xhr.onerror = () => resolve(null)
      xhr.send()
    })
  }

  // file:// URI de nuestro almacenamiento → Filesystem.readFile
  try {
    const { data } = await Filesystem.readFile({ path: localPath })
    const binary = atob(data)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return new Blob([bytes], { type: 'image/jpeg' })
  } catch {
    return null
  }
}

export const deleteImageFromFileSystem = async localPath => {
  if (!Capacitor.isNativePlatform()) return
  try {
    await Filesystem.deleteFile({ path: localPath })
  } catch (e) {
    console.error('deleteImageFromFileSystem', e)
  }
}

export function sanitizeBase64 (raw, original = null) {
  if (!raw || typeof raw !== 'string') return original || null
  let b64 = raw.trim()

  if (!b64.startsWith('data:image')) {
    const comma = b64.indexOf(',')
    if (comma > -1) b64 = 'data:image/jpeg;base64,' + b64.substring(comma + 1)
    else return original || null
  }

  if (b64.length > MAX_BASE64_SIZE) return null
  return b64
}

export async function loadAndCompress (localPath, maxWidth = 1200) {
  const base64 = await readImageFromFileSystem(localPath)
  if (!base64) return null
  return compressBase64(base64, maxWidth)
}
